import useToastConfirmation from "../../../../components/common/toasts/useToastConfirmation";
import { useToast } from "../../../../components/common/toasts/ToastContext";
import ErrorToast from "../../../../components/common/toasts/ErrorToast";
import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { premiumRequest } from "../../../../services/Premium";
import { Field } from "../Fields";
import DictionaryEntries from "./DictionaryEntries";
import DictionarySelector from "./DictionarySelector";
import usePremiumResource from "../../hooks/usePremiumResource";
import useAsyncAction from "../../hooks/useAsyncAction";
import { filterDictionaryEntries } from "./dictionaryUtils";
import { premiumPaths } from "../../navigation";

/** Lista wpisów; formularze dodawania i edycji otwierają osobne strony. */
export default function DictionaryPanel({
  organization,
  token,
  definition,
  definitions,
  admin,
}) {
  const [params, setParams] = useSearchParams();
  const { notify } = useToast();
  const { confirm } = useToastConfirmation(organization.id);
  const navigate = useNavigate();
  const url = `/organizations/${organization.id}/dictionaries`;
  const resource = usePremiumResource(token, url);
  const { busy: saving, error: saveError, run } = useAsyncAction(url);
  const busy = saving || resource.loading || !resource.data;
  const remove = (entry) => {
    run(async (isActive) => {
      if (
        !(await confirm(`Usunąć wpis ${entry.key}?`, {
          acceptLabel: "Usuń wpis",
        })) ||
        !isActive()
      )
        return;
      const result = await premiumRequest(token, url, {
        method: "PUT",
        data: { kind: definition.kind, key: entry.key, remove: true },
      });
      if (isActive()) {
        resource.replace(result);
        notify("Usunięto wpis.");
      }
    });
  };
  const entries = (resource.data?.entries || []).filter(
    (item) => item.kind === definition.kind,
  );
  return (
    <section className="card premium-panel">
      <div className="premium-toolbar premium-dictionary-toolbar">
        <DictionarySelector
          organizationId={organization.id}
          definitions={definitions}
          definition={definition}
          admin={admin}
        />
        {organization.manageDictionaries && (
          <Link
            className="btn btn-primary"
            to={premiumPaths.newDictionaryEntry(
              organization.id,
              definition.kind,
              admin,
            )}
          >
            Dodaj wpis
          </Link>
        )}
      </div>
      <p className="small text-muted">
        {organization.config[definition.enabledBy]
          ? "Słownik jest włączony."
          : "Słownik jest wyłączony. Administrator może go włączyć w ustawieniach firmy."}{" "}
        Zmiany wpisów obowiązują dla nowych zleceń.
      </p>
      <ErrorToast message={resource.error || saveError} />
      {resource.loading && <p role="status">Ładowanie danych…</p>}
      {resource.error && (
        <button className="btn btn-outline-primary" onClick={resource.reload}>
          Ponów odczyt
        </button>
      )}
      {!organization.manageDictionaries && (
        <p className="small text-muted">
          Masz dostęp do odczytu. Prawo edycji przydziela administrator firmy.
        </p>
      )}
      <Field
        label="Szukaj wpisu"
        value={params.get("q") || ""}
        onChange={(event) => {
          const next = new URLSearchParams(params);
          if (event.target.value) next.set("q", event.target.value);
          else next.delete("q");
          setParams(next, { replace: true });
        }}
      />
      <DictionaryEntries
        definition={definition}
        entries={filterDictionaryEntries(entries, params.get("q") || "")}
        editable={organization.manageDictionaries}
        disabled={busy}
        onEdit={(entry) =>
          navigate(
            premiumPaths.editDictionaryEntry(
              organization.id,
              definition.kind,
              entry.key,
              admin,
            ),
          )
        }
        onRemove={remove}
      />
    </section>
  );
}
